import { useState, useRef, useEffect } from 'react';

interface NextGenImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
  style?: React.CSSProperties;
}

export const NextGenImage = ({
  src,
  alt,
  className = '',
  priority = false,
  loading = 'lazy',
  fetchPriority = 'auto',
  decoding = 'async',
  style
}: NextGenImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority || loading === 'eager') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority, loading]);

  // Only create modern format sources if they actually exist
  // For now, let's be more conservative and only use original sources
  const webpSrc = src.includes('.webp') ? src : null;
  const avifSrc = src.includes('.avif') ? src : null;

  if (hasError) {
    return (
      <div className={`bg-muted/20 flex items-center justify-center ${className}`} ref={imgRef}>
        <span className="text-muted-foreground text-sm">Image unavailable</span>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} ref={imgRef} style={style}>
      {/* Loading placeholder with smooth transition */}
      {!isLoaded && isInView && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-primary/20 animate-pulse" />
      )}
      {isInView && (
        <>
          {(webpSrc || avifSrc) ? (
            <picture>
              {avifSrc && <source srcSet={avifSrc} type="image/avif" />}
              {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
              <img
                src={src}
                alt={alt}
                className={`w-full h-full object-cover transition-opacity duration-500 ${
                  isLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                loading={loading}
                {...((fetchPriority && fetchPriority !== 'auto') ? { fetchpriority: fetchPriority } : {}) as any}
                decoding={decoding}
                onLoad={() => setIsLoaded(true)}
                onError={() => setHasError(true)}
                style={{
                  willChange: 'auto',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)'
                }}
              />
            </picture>
          ) : (
            <img
              src={src}
              alt={alt}
              className={`w-full h-full object-cover transition-opacity duration-500 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading={loading}
              {...((fetchPriority && fetchPriority !== 'auto') ? { fetchpriority: fetchPriority } : {}) as any}
              decoding={decoding}
              onLoad={() => setIsLoaded(true)}
              onError={() => setHasError(true)}
              style={{
                willChange: 'auto',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)'
              }}
            />
          )}
        </>
      )}
    </div>
  );
};